import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDiff, Loader2, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/** "Variations" button on a draft RA bill: shows the variation work already on the bill and lets you add billable lines.
 *  Only client-accepted variations are offered (or approved ones, when the organisation doesn't need client acceptance). */
export function RABillVariationLines({ bill, canEdit = true, onChanged }) {
  const [open, setOpen] = useState(false);
  const [onBill, setOnBill] = useState([]);
  const [billable, setBillable] = useState([]);
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: rows }, { data: lines }] = await Promise.all([
        api.get(`/revenue/ra-bills/${bill.id}/variation-lines`),
        api.get(`/projects/${bill.project_id}/variations/billable-lines`).catch(() => ({ data: [] })),
      ]);
      setOnBill(rows || []); setBillable((lines || []).filter((l) => Number(l.left) > 0));
    } catch (err) { toast.error(getErrorMessage(err)); }
  }, [bill.id, bill.project_id]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const add = async (l) => {
    setBusy(true);
    try {
      await api.post(`/revenue/ra-bills/${bill.id}/variation-lines`, { variation_line_id: l.variation_line_id, quantity: qty[l.variation_line_id] });
      setQty((q) => ({ ...q, [l.variation_line_id]: "" }));
      toast.success("Added to the bill. Its gross amount, tax, retention and net are updated.");
      load(); onChanged?.();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };
  const remove = async (r) => {
    setBusy(true);
    try { await api.delete(`/revenue/ra-bills/${bill.id}/variation-lines/${r.id}`); load(); onChanged?.(); } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };
  const total = onBill.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)} title="Variation work on this bill" data-testid={`ra-bill-variations-${bill.number}`}>
        <FileDiff className="h-3.5 w-3.5" />
      </Button>
      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl" data-testid="ra-bill-variations-dialog">
            <DialogHeader><DialogTitle>Variation work on {bill.number}</DialogTitle>
              <DialogDescription>Only variations the client has accepted can be billed. Lines you add or remove here change the bill's gross amount, tax, retention and net.</DialogDescription></DialogHeader>
            <div className="space-y-4 text-sm">
              <div>
                <div className="mb-1 font-medium">On this bill</div>
                {!onBill.length ? <p className="text-muted-foreground">None yet.</p> : (
                  <ul className="divide-y rounded-lg border">{onBill.map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-2 px-3 py-2">
                      <span>{r.variation_number} · {r.description} · {Number(r.quantity)} {r.unit || ""} × {inr(r.rate)}</span>
                      <span className="flex items-center gap-2 tabular-nums">{inr(r.amount)}
                        {canEdit && bill.status === "DRAFT" && <button type="button" aria-label="Remove" disabled={busy} onClick={() => remove(r)} className="rounded p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                      </span>
                    </li>))}</ul>
                )}
                {onBill.length > 0 && <p className="mt-1 text-right font-semibold" data-testid="ra-bill-variations-total">Variation total {inr(total)}</p>}
              </div>
              {canEdit && bill.status === "DRAFT" && (
                <div>
                  <div className="mb-1 font-medium">Ready to bill</div>
                  {!billable.length ? <p className="text-muted-foreground">No accepted variation work left to bill on this project.</p> : (
                    <ul className="divide-y rounded-lg border">{billable.map((l) => (
                      <li key={l.variation_line_id} className="flex items-center gap-2 px-3 py-2">
                        <span className="min-w-0 flex-1 truncate">{l.variation_number} · {l.description} <span className="text-xs text-muted-foreground">({Number(l.left)} {l.unit || ""} left at {inr(l.rate)})</span></span>
                        <Input className="h-8 w-24" type="number" min="0" max={l.left} step="any" value={qty[l.variation_line_id] || ""} onChange={(e) => setQty((q) => ({ ...q, [l.variation_line_id]: e.target.value }))} aria-label={`Quantity for ${l.description}`} />
                        <Button size="sm" variant="outline" disabled={busy || !(Number(qty[l.variation_line_id]) > 0)} onClick={() => add(l)}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add</Button>
                      </li>))}</ul>
                  )}
                </div>
              )}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default RABillVariationLines;
